import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merge Tailwind class names, resolving conflicts (shadcn convention).
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Single categorical data-viz ramp (016 design tokens, doc §1): teal-anchored,
 * legible on white. Used by every chart so series colours are consistent across pages.
 */
// Categorical series palette, brand teal first. Ordered for maximal hue
// separation in the first ~5 slots (the common case) and with the near-teal
// cyan moved to LAST so it no longer sits next to #107169 and gets confused with
// it (review v2 §1.2). First five hues: teal / orange / indigo / violet / pink.
// The first eight are the original, maximally-separated hues (kept first and
// unchanged so every other chart's colours are stable); the next eight extend
// the palette so features with many series — e.g. Failure Clusters — can give
// more categories a distinct colour before any wrap/reuse.
export const CHART_COLORS = [
  "#107169", // teal (brand)
  "#C2410C", // orange
  "#3F54B1", // indigo
  "#7C3AED", // violet
  "#BE185D", // pink
  "#15803D", // green
  "#B45309", // amber
  "#0E7490", // cyan (kept distinct from the brand teal)
  "#DC2626", // red
  "#2563EB", // blue
  "#65A30D", // lime
  "#C026D3", // fuchsia
  "#CA8A04", // gold
  "#059669", // emerald
  "#E11D48", // rose
  "#0284C7", // sky
] as const;

/**
 * Formats a number with locale-specific formatting.
 * Returns "Unlimited" if the value is null.
 */
export const formatNumber = (value: number | null): string => {
  if (value === null) return "N/A";
  return value.toLocaleString();
};

/**
 * Formats a rate value with "/min" suffix.
 * Returns "Unlimited" if the value is null.
 */
export const formatRate = (rate: number | null): string => {
  if (rate === null) return "N/A";
  return `${rate.toLocaleString()}/min`;
};

/**
 * Masks an API key by showing only the first 4 and last 4 characters.
 * Returns the full key if it's 8 characters or less.
 */
export const maskApiKey = (key: string): string => {
  if (key.length <= 8) return key;
  return `${key.slice(0, 4)}...${key.slice(-4)}`;
};

/**
 * Formats a date string to ISO format YYYY/MM/DD.
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}/${month}/${day}`;
};

/**
 * Formats latency in milliseconds to a human-readable string.
 * Returns "XXXms" for values < 1000ms, "X.XXs" for values >= 1000ms.
 */
export const formatLatency = (ms: number): string => {
  if (ms < 1000) {
    return `${ms}ms`;
  }
  return `${(ms / 1000).toFixed(2)}s`;
};

